export type SortDirection = 'asc' | 'desc' | 'none';

export interface SortConfig {
  key: string;
  direction: SortDirection;
}

export const getNextSortDirection = (currentDirection: SortDirection): SortDirection => {
  switch (currentDirection) {
    case 'none':
      return 'asc';
    case 'asc':
      return 'desc';
    case 'desc':
      return 'none';
    default:
      return 'asc';
  }
};

export const sortData = <T>(
  data: T[],
  sortConfig: SortConfig,
  getValue: (item: T, key: string) => any
): T[] => {
  if (sortConfig.direction === 'none') {
    return [...data]; // Return a copy to maintain immutability
  }

  return [...data].sort((a, b) => {
    const aValue = getValue(a, sortConfig.key);
    const bValue = getValue(b, sortConfig.key);

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
    if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

    // Handle different data types
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      const comparison = aValue - bValue;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }

    if (aValue instanceof Date && bValue instanceof Date) {
      const comparison = aValue.getTime() - bValue.getTime();
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }

    // Handle mixed types - convert to string for comparison
    const aStr = String(aValue);
    const bStr = String(bValue);
    const comparison = aStr.localeCompare(bStr, undefined, { numeric: true });
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });
};

export const getSortIcon = (direction: SortDirection) => {
  switch (direction) {
    case 'asc':
      return '↑';
    case 'desc':
      return '↓';
    case 'none':
    default:
      return '↕';
  }
};
